import React, { useRef, useState, useMemo } from 'react';
import Form, { FormInputComponent } from "../../../components/form/Form";
import { useTalentTeam } from "../../../providers/TalentTeamProvider";
import ErrorBanner from "../../../components/ErrorBanner";
import LoadingBanner from "../../../components/LoadingBanner";
import { ModalHeadless } from '../../../components/Modal';
import TeamServicesModal from './TeamServicesModal';
import { Navigate } from "react-router-dom";
import {useAPI} from "../../../config/API";
// import ItemSelectModal from '../../../components/ItemSelectModal';

const TeamServicesStep = ({ isManaging }) => {
    const {
        team,
        isUpdating,
        updateTeamServices,
        onModelChange,
    } = useTalentTeam();
    const [showServiceModal, setShowServiceModal] = useState(false);
    // const [categories, setCategories] = useState([]);

    const formRef = useRef(null);
    const {
        data: talent_service_groups,
        isFetching,
        isLoading,
        isRefetching,
        error,
        refetch
    } = useAPI('fetchAllServices')

    const disciplines = useMemo(() => {
        return TB.filter([...TB.uniqBy(TB.flatMap(TB.map(team?.talent_service_groups, 'disciplines')), 'id')],['is_optional', false]);
    }, [team?.talent_service_groups])

    const omitServices = useMemo(() => {
        let serviceIds = TB.map(team?.talent_service_groups || [], 'id');
        if (serviceIds.length) {
            return "?omit[]=" + TB.join(serviceIds, '&omit[]=')
        }
        return false
    }, [team?.talent_service_groups, isUpdating])

    const activeServices = useMemo(() => {
        return TB.filter(talent_service_groups, service => service.disciplines?.length > 0)
    }, [talent_service_groups, isLoading, isUpdating, team?.talent_service_groups]);

    const selectedCategories = useMemo(() => {
        return TB.groupBy(team?.talent_service_groups || [], 'category.name');
    }, [team.talent_service_groups, isLoading, team])


    /**
     * Close the catalogue modal and clear the search field's form state.
     *
     * Selecting from the modal calls `updateTeamServices` directly rather than
     * going through the search input, so the input keeps whatever formsy last
     * held. The search path already resets on change — this gives the modal path
     * the same treatment.
     */
    const closeServiceModal = () => {
        setShowServiceModal(false);
        try {
            formRef.current?.formRef?.current?.formsyForm.reset();
        } catch (e) {
            console.warn('ERROR: e01', e)
        }
    }

    if (!team.id) {
        return <Navigate to="../details" replace={true} />
    }
    let teamServiceIds = TB.map(team.talent_service_groups, 'id')

    const renderStatusLabel = (service) => {
        let statusLabel, iconClass = 'h-4 w-4 rounded-full ';
        if (service.is_approved) {
            statusLabel = 'Approved';
            iconClass += 'bg-success';
        } else if (service.approval_requested) {
            statusLabel = 'Approval Requested';
            iconClass += 'bg-[#4B80E7]';
        } else if (!service.has_missing_required_disciplines) {
            statusLabel = 'Ready for Approval';
            iconClass += 'bg-warning';
        } else if (service.has_missing_required_disciplines) {
            statusLabel = 'Missing Required Roles';
            iconClass += 'bg-danger';
        } else {
            statusLabel = 'Missing Roles';
            iconClass += 'bg-danger';
        }

        return (
            <div className="flex items-center gap-3 pt-1 md:pt-0">
                <p className="font-normal md:font-bold">{statusLabel}</p>
                <div className={iconClass} />
            </div>
        )
    }


    return (
        <div>
            <div className="form-title-section">
                <div className="step-title-content">
                    <div>
                        <div className="flex items-center pb-4">
                            <h3 className="form-title !mb-0">
                                Project Types {team?.talent_service_groups?.length > 0 ? `(${team.talent_service_groups.length})` : ''}
                            </h3>
                        </div>

                        <h5 className="form-subtitle">
                            What types of projects your team is interested in working on? Note: project types will impact the specific roles and skills you will need on this team.
                        </h5>
                    </div>
                </div>
            </div>

            <div className="talent-role-form">
                <Form
                    ref={formRef}
                    title={null}
                    subtitle={null}
                    showRequired
                    submitButton={false}
                    onChange={async (model) => {
                        try {
                            formRef.current?.formRef?.current?.formsyForm.reset();
                        } catch (e) {
                            console.warn('ERROR: e00', e)
                        }
                        if (typeof model?.service === 'object' && model?.service.id && !TB.find(team.talent_service_groups || [], ['id', model?.service.id])) {
                            let didUpdate = await updateTeamServices([...(team.talent_service_groups || []), model?.service]);

                            if (didUpdate) {
                                await TB.wait(150)
                                let serviceInfoElem = document.getElementById(`service-details-${model?.service.id}`)
                                if (serviceInfoElem) {
                                    $('html, body').animate({ scrollTop: $(serviceInfoElem).offset().top }, 1000);
                                }
                            }
                        }
                    }}
                    model={team}
                    fields={[]}
                    isDisabled={false}>


                    {!isLoading && error && <ErrorBanner title={"Something Went Wrong"} onRetry={refetch} />}

                    {isLoading && <LoadingBanner message="Loading project types" />}

                    {!isLoading && !error && activeServices.length > 0 && (
                        <div className="flex flex-col items-stretch">
                            <div className="field-section flex-row items-start">
                                <div className="field basis-10/12">
                                    <FormInputComponent
                                        value={team?.talent_service_groups || ''}
                                        fieldName="service"
                                        type="search"
                                        searchType="talent_service_groups"
                                        placeholder="(e.g. Websites, Apps, Videos, SEO, etc.) "
                                        valueLabelDisplay="name"
                                        disabled={isUpdating}
                                        label={(
                                            <div>
                                                <h3 className="text-xl pb-3">Team Project Types</h3>
                                                <p className="text-xs pb-1">Select the types of Wripple projects you
                                                    want your
                                                    team to work on.</p>
                                            </div>
                                        )}
                                        required={false}
                                        // suggestions={availableServices}
                                        searchRequestEndpoint={`/api/talent-service-groups${omitServices || ''}`}
                                        isSuggestionAvailable={suggestion => {
                                            return !suggestion?.id || (suggestion?.disciplines.length > 0 && !teamServiceIds.includes(suggestion.id))
                                        }}
                                        columns={8}
                                        validations={{
                                            serviceNotSelected: ({ service }, value) => {
                                                if (!service) return;

                                                // On re-render the field's value is the team's current
                                                // selections (an array), not an in-progress pick. Adding
                                                // from the catalogue modal bypasses this input entirely,
                                                // so without this guard the leftover array fails the
                                                // `value.id` check and shows a bogus error.
                                                if (Array.isArray(value)) return true;

                                                if (!TB.isDefined(value?.id)) {
                                                    return "Please select a valid type."
                                                }

                                                return TB.isDefined(value?.id) ? true : TB.trans('validation.required', {
                                                    'attribute': "service",
                                                })
                                            },
                                        }}
                                        appendAfter={(
                                            <button className="btn btn-primary mt-3" onClick={() => setShowServiceModal(true)}>
                                                Add/View All Project Types
                                            </button>
                                        )}
                                    />
                                </div>
                            </div>

                            <hr className="mt-5 mb-8" />

                            <div>
                                <h3 className="text-xl">Current Selections</h3>

                                {!TB.keys(selectedCategories)?.length && (
                                    <p className="pt-3 text-xs">Add a project type from the options above</p>
                                )}

                                {TB.keys(selectedCategories)?.length > 0 && TB.map(selectedCategories, (talent_service_groups, category) => (
                                    <div key={category}>
                                        <p className="font-primary font-bold text-xs uppercase text-primary tracking-wide py-4">{category}</p>
                                        {talent_service_groups?.map(service => {
                                            const [optionalRoles, requiredRoles] = TB.partition(service.disciplines, 'is_optional');

                                            return (
                                                <div key={service.id} id={`service-details-${service.id}`} className="bg-wrippleOffWhite p-3 mb-4">
                                                    <div className="flex justify-between items-center md:gap-10">
                                                        <div className="md:flex justify-between w-full">
                                                            <p className="font-bold text-sm">{service.name}</p>

                                                            {!!isManaging && renderStatusLabel(service)}
                                                        </div>

                                                        <button type="button" disabled={isUpdating} className="p-1"
                                                            onClick={async () => {
                                                                let confirm = await TB.sysAlert('talent_teams.remove_project_type_confirm')
                                                                if (confirm) {
                                                                    return updateTeamServices(TB.xorBy([...(team.talent_service_groups || [])], [service], 'id'))
                                                                }
                                                            }}>
                                                            <i className="fas fa-times" />
                                                        </button>
                                                    </div>

                                                    {!!service.disciplines?.length && (
                                                        <div className="pt-2 space-y-3">
                                                            <div>
                                                                <p className="font-bold text-xs text-[#6A6D74]">Required Roles:</p>
                                                                <p className="text-xs text-[#6A6D74]">
                                                                    {requiredRoles?.length > 0 && requiredRoles.map((discipline, index) => {
                                                                        return <span key={discipline.id}>{(index ? ', ' : '') + discipline.name}</span>
                                                                    }) || 'None'}
                                                                </p>
                                                            </div>

                                                            <div>
                                                                <p className="font-bold text-xs text-[#6A6D74]">Other Associated Roles:</p>
                                                                <p className="text-xs text-[#6A6D74]">
                                                                    {optionalRoles?.length > 0 && optionalRoles.map((discipline, index) => {
                                                                        return <span key={discipline.id}>{(index ? ', ' : '') + discipline.name}</span>
                                                                    }) || 'None'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>
                                )
                                )}
                            </div>
                            {disciplines?.length > 0 && (
                                <React.Fragment>
                                    <hr className="mt-5 mb-8" />
                                    <div>
                                        <h3 className="text-xl">Required Roles</h3>

                                        <p className="text-xs pt-3 pb-5">
                                            Based upon the selected project types, the required roles on your team are included below.
                                        </p>

                                        <div className="flex flex-row gap-1.5 items-center justify-between flex-wrap">
                                            {disciplines.map(discipline => {
                                                return (
                                                    <p key={discipline.id}
                                                       className="font-bold basis-5/12 whitespace-nowrap flex-grow">
                                                        {discipline.name}
                                                    </p>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </React.Fragment>
                            )}
                        </div>
                    )}
                </Form>
            </div>
            <ModalHeadless isVisible={showServiceModal} onCloseAction={closeServiceModal} closeOnBackdrop>
                <TeamServicesModal
                    onClose={closeServiceModal}
                    services={activeServices}
                />
            </ModalHeadless>
            {/* <ModalHeadless isVisible={showServiceModal} onCloseAction={() => setShowServiceModal(false)} closeOnBackdrop>
                <div className="bg-white p-4">
                    <div className="flex justify-between border-b border-[#C6C6C6] pb-6 mb-8">
                        <h3>Wripple Project Descriptions</h3>

                        <button className="p-1 flex self-start text-xl" onClick={() => setShowServiceModal(false)}>
                            <i className="fas fa-times-circle" />
                        </button>
                    </div>

                    <ItemSelectModal
                        fieldName="services"
                        selectText="Select"
                        multiSelect={true}
                        selectedIds={teamServiceIds}
                        isDisabled={false}
                        categoryHeader="Project Category"
                        itemHeader="Project Type"
                        items={activeServices}
                        hideModal={() => setShowServiceModal(false)}
                        toggleItem={service => {
                            const services = TB.xorBy([...(team.services || [])], [{...service}], 'id');
                            updateTeamServices(services)
                        }}
                    />
                </div>
            </ModalHeadless> */}
        </div>
    );
};

TeamServicesStep.propTypes = {};

export default TeamServicesStep;